import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from './supabase';
import { ArrowRight, MessageCircle, Mail, Clock, ShieldAlert, Eye, Send, AlertTriangle, ChevronRight } from 'lucide-react';
import { WA_TEMPLATE_BODIES } from './waTemplates';
import { WA_RATES_USD } from './WaUsage';
import { cn } from './ui/Table';
import { Badge } from './ui/arc/badge';
import { Dialog, DialogContent } from './ui/arc/dialog';

// Mapa de mensajes a clientes (30/10/2026): el camino completo de las notificaciones de WhatsApp
// (y a futuro emails) como diagrama por etapas, con el texto real de cada template, qué lo dispara,
// cuándo sale, condiciones y números en vivo de los últimos 30 días (vista wa_template_stats_30d).
// La DEFINICIÓN de los flujos vive acá abajo (FLOW): si cambia un flujo, se actualiza este archivo.

type Channel = 'whatsapp' | 'email';
interface Msg {
  id: string;
  title: string;
  templates: string[];          // template(s) de Meta (el 1º es el principal); '_texto' = texto libre
  channel: Channel;
  trigger: string;              // qué lo dispara
  when: string;                 // cuándo sale
  conditions?: string[];        // reglas / excepciones
  next?: string[];              // títulos de lo que puede seguir
  variant?: string;             // etiqueta chica (A/B, Epic Week…)
  note?: string;
}
interface Stage { id: string; title: string; subtitle: string; msgs: Msg[] }

const FLOW: Stage[] = [
  {
    id: 'compra', title: '1 · Compra', subtitle: 'Al crearse o pagarse el pedido',
    msgs: [
      { id: 'conf', title: 'Pedido confirmado', templates: ['order_confirmation_util', 'order_confirmation_epic_week_v2'], channel: 'whatsapp',
        trigger: 'Pago confirmado (TN pagada o venta manual marcada pagada), no efectivo', when: 'Al instante',
        conditions: ['Nunca a MercadoLibre', 'Requiere teléfono', 'Una sola vez por pedido (no se repite al re-marcar pagada)', 'Epic Week: versión _epic_week_v2 (26/10–01/11)'] },
      { id: 'cash', title: 'Efectivo + retiro', templates: ['cash_order_confirmation', 'cash_order_confirmation_epic_week_v2'], channel: 'whatsapp',
        trigger: 'Pedido con pago en efectivo y retiro en el local', when: 'Al instante', conditions: ['Paga al retirar'] },
      { id: 'cashenvio', title: 'Efectivo + envío (botones)', templates: ['cash_shipping_decision'], channel: 'whatsapp',
        trigger: 'Eligió efectivo pero con envío (no se puede)', when: 'Al instante',
        next: ['"Voy a retirar" → pasa a retiro + dirección y horario', '"Prefiero envío" → pasa a transferencia + datos (transferencia_datos)'] },
      { id: 'transf', title: 'Datos de transferencia', templates: ['transferencia_datos'], channel: 'whatsapp',
        trigger: 'Transferencia "a convenir" (TN) o "Prefiero envío"', when: 'Al instante', next: ['Recordatorio a las 5 h', 'Acuse de comprobante'] },
      { id: 'recor', title: 'Recordatorio de transferencia', templates: ['transferencia_recordatorio'], channel: 'whatsapp',
        trigger: 'No pagó ni mandó comprobante', when: '5 h después de los datos',
        conditions: ['Entre 9 y 21 hs', 'No si mandó comprobante o ya pagó otra orden igual'], next: ['Seguimiento 24 h después'] },
      { id: 'segui', title: 'Seguimiento de transferencia', templates: ['transferencia_seguimiento_v2'], channel: 'whatsapp',
        trigger: 'Sigue sin pagar y no escribió después del recordatorio', when: '24 h después del recordatorio',
        conditions: ['Una vez por pedido', 'La respuesta la interpreta el agente (cancelar → escala a humano)'] },
      { id: 'acuse', title: 'Acuse de comprobante', templates: ['_texto'], channel: 'whatsapp',
        trigger: 'Manda imagen/PDF teniendo una transferencia pendiente', when: 'Al instante', conditions: ['Uno cada 15 min', 'Aviso (campanita) al equipo'] },
    ],
  },
  {
    id: 'envio', title: '2 · Preparación y envío', subtitle: 'Según cómo se entrega',
    msgs: [
      { id: 'retiro', title: 'Listo para retirar', templates: ['pedido_listo_retiro'], channel: 'whatsapp',
        trigger: 'Pasa a Empaquetado y es retiro en el local', when: '~1,5 min después (cola)', conditions: ['No si es transferencia sin pagar', 'Lo manda solo la cola (no la card)'] },
      { id: 'cabhoy', title: 'Cabify: te llega hoy', templates: ['pedido_llega_hoy_cabify'], channel: 'whatsapp',
        trigger: 'Cabify retiró el paquete (centro de distribución)', when: 'Al instante (webhook de Cabify)' },
      { id: 'cabcam', title: 'Cabify: en camino', templates: ['pedido_en_camino_cabify'], channel: 'whatsapp',
        trigger: 'El repartidor sale con el paquete', when: 'Al instante (webhook de Cabify)' },
      { id: 'correo', title: 'Correo / punto de retiro', templates: [], channel: 'whatsapp',
        trigger: 'Andreani / Envío Nube / Punto de retiro', when: '—', note: 'Sin WhatsApp: avisa Tiendanube. La orden queda "Despachado" hasta que TN informa la entrega.' },
    ],
  },
  {
    id: 'post', title: '3 · Post-venta', subtitle: 'Después de la entrega',
    msgs: [
      { id: 'review', title: 'Pedido de reseña', templates: ['review_request_initial_v4'], channel: 'whatsapp',
        trigger: 'Pedido entregado', when: '2 días (Cabify / retiro) · 7 días (correo)',
        conditions: ['Nunca a MercadoLibre', 'Un solo intento'], next: ['Rating → comentario → foto → cupón 10% (texto)'] },
      { id: 'foto', title: 'Recordatorio de foto', templates: ['review_foto_recordatorio'], channel: 'whatsapp',
        trigger: 'Calificó bien pero no mandó foto', when: '3 h después', conditions: ['Entre 9 y 21 hs'] },
      { id: 'cupon', title: 'Cupón de reseña', templates: ['_texto'], channel: 'whatsapp',
        trigger: 'Mandó la foto', when: 'Al instante', conditions: ['Cupón KRI-… 10%, 1 solo uso, vence a los 3 meses', 'Un solo cupón aunque mande varias fotos'] },
    ],
  },
  {
    id: 'carritos', title: '4 · Carritos abandonados', subtitle: 'Empezó a comprar y no terminó',
    msgs: [
      { id: 'pr', title: 'Pago rechazado', templates: ['pago_rechazado_ayuda'], channel: 'whatsapp', variant: 'si intentó pagar',
        trigger: 'Intentó pagar (tarjeta / Pago Nube) y no se cobró', when: '30 min',
        conditions: ['Reemplaza al 1er mensaje', 'No lo frena el cooldown', 'Cualquier respuesta → pasa a una persona'] },
      { id: 'epic1', title: 'Epic Week: urgencia', templates: ['carrito_abandonado_epic_week'], channel: 'whatsapp', variant: 'Epic Week',
        trigger: 'Carrito durante la Epic Week', when: '30 min', conditions: ['Una vez por persona en la semana', 'Sin cupón (no se suma descuento)'] },
      { id: 'epic2', title: 'Epic Week: último aviso', templates: ['carrito_epic_ultimo_aviso'], channel: 'whatsapp', variant: 'Epic Week',
        trigger: 'No compró después del de urgencia', when: '24 h', conditions: ['Cuenta regresiva: "quedan N días" / "pocas horas"'] },
      { id: 'b1', title: 'B · Foto + botón', templates: ['carrito_abandonado_v3'], channel: 'whatsapp', variant: 'A/B · B',
        trigger: 'Carrito abandonado (mitad de los carritos)', when: '30 min', next: ['Ayuda (24 h)', 'Cupón (72 h)'] },
      { id: 'b2', title: 'B · Ayuda (cuotas, envíos)', templates: ['carrito_abandonado_ayuda'], channel: 'whatsapp', variant: 'A/B · B',
        trigger: 'No compró ni respondió', when: '24 h', note: 'Meta lo bloquea seguido (exceso de marketing).' },
      { id: 'c1', title: 'C · Mensaje personal de Martín', templates: ['carrito_personal_martin'], channel: 'whatsapp', variant: 'A/B · C',
        trigger: 'Carrito abandonado (otra mitad)', when: '30 min', conditions: ['Sin link: si responde, el agente ve el carrito y le pasa el link'], next: ['Cupón (48 h)'] },
      { id: 'cupon5', title: 'Cupón 5% (VOLVE)', templates: ['carrito_abandonado_insistencia'], channel: 'whatsapp',
        trigger: 'Sigue sin comprar', when: 'C: 48 h · B: 72 h', conditions: ['1 uso, vence a las 48 h', 'No en Epic Week'] },
      { id: 'viejo', title: 'A · Texto + link (retirada)', templates: ['carrito_abandonado_v2'], channel: 'whatsapp', variant: 'fuera de la prueba',
        trigger: 'Perdió el A/B (16% vs 26%)', when: '—' },
    ],
  },
  {
    id: 'atencion', title: '5 · Conversación', subtitle: 'Cuando el cliente escribe',
    msgs: [
      { id: 'agente', title: 'Agente automático', templates: ['_texto'], channel: 'whatsapp',
        trigger: 'El cliente escribe', when: 'Al instante (dentro de la ventana de 24 h)',
        conditions: ['No responde emojis / "gracias" / "dale" sueltos (salvo si le preguntamos algo)', 'Reclamos, precios especiales y dudas → escala a una persona', '"¿Cuándo llega?" → link de seguimiento con fechas estimadas; si ya lo tenía y vuelve a preguntar → escala', 'Nunca promete un horario de entrega', 'Nunca datos bancarios ni precios inventados'] },
    ],
  },
  {
    id: 'email', title: 'Email', subtitle: 'Canal a sumar',
    msgs: [
      { id: '2fa', title: 'Código de ingreso al admin', templates: [], channel: 'email', trigger: 'Login del equipo', when: 'Al instante', note: 'Interno (Resend, info@krilic.com.ar).' },
      { id: 'pagomail', title: 'Pago rechazado (email)', templates: [], channel: 'email', trigger: 'Intentó pagar y no se cobró', when: '—', note: 'Armado pero sin usar (se eligió WhatsApp).' },
    ],
  },
];

interface Stat { template_name: string; enviados: number; entregados: number; leidos: number; fallidos: number; ultimo: string }

export function MapaMensajes() {
  const [stats, setStats] = useState<Map<string, Stat>>(new Map());
  const [cats, setCats] = useState<Map<string, string>>(new Map());
  const [open, setOpen] = useState<Msg | null>(null);

  useEffect(() => {
    supabase.from('wa_template_stats_30d').select('*').then(({ data }) =>
      setStats(new Map(((data || []) as Stat[]).map(s => [s.template_name, s]))));
    supabase.functions.invoke('wa-template-get', { body: { list: true } }).then(({ data }) =>
      setCats(new Map(((data?.templates || []) as any[]).map(t => [t.name, String(t.category || '').toUpperCase()]))));
  }, []);

  // '_texto' agrupa TODO el texto libre (agente, acuses, cupones…): no se atribuye a un mensaje puntual
  const statOf = (m: Msg): Stat[] | null => {
    const ss = m.templates.filter(t => t !== '_texto').map(t => stats.get(t)).filter(Boolean) as Stat[];
    return ss.length ? ss : null;
  };
  const sum = (ss: Stat[] | null) => ss && ss.reduce((a, s) => ({ enviados: a.enviados + Number(s.enviados), entregados: a.entregados + Number(s.entregados), leidos: a.leidos + Number(s.leidos), fallidos: a.fallidos + Number(s.fallidos) }), { enviados: 0, entregados: 0, leidos: 0, fallidos: 0 });
  const costOf = (m: Msg, n: number) => {
    const c = cats.get(m.templates[0]);
    if (!c || !n) return null;
    return n * (c === 'MARKETING' ? WA_RATES_USD.marketing : WA_RATES_USD.utility);
  };
  const totals = useMemo(() => {
    let env = 0, usd = 0;
    for (const st of FLOW) for (const m of st.msgs) {
      const s = sum(statOf(m)); if (!s) continue; env += s.enviados; usd += costOf(m, s.enviados) || 0;
    }
    return { env, usd };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stats, cats]);

  return (
    <div className="tw w-full min-w-0 pb-12 pt-1">
      <header className="mb-5">
        <h1 className="text-[length:var(--text-2xl)] font-semibold leading-tight tracking-[var(--tracking-display)] text-ink">Mapa de mensajes</h1>
        <p className="mt-1 max-w-[80ch] text-[length:var(--text-sm)] text-soft">
          Todo lo que le llega a un cliente, en el orden en que le llega. Tocá un mensaje para ver el texto real, qué lo dispara y sus números de los últimos 30 días.
          {totals.env > 0 && <> · <b className="text-ink tabular-nums">{totals.env.toLocaleString('es-AR')}</b> templates enviados en 30 días (~USD {totals.usd.toFixed(0)}).</>}
        </p>
      </header>

      <div className="flex items-stretch gap-3 overflow-x-auto pb-3">
        {FLOW.map((st, i) => (
          <React.Fragment key={st.id}>
            {i > 0 && st.id !== 'email' && <div className="flex shrink-0 items-start pt-10 text-faint"><ArrowRight size={18} /></div>}
            <section className={cn('flex w-72 shrink-0 flex-col rounded-[var(--radius-panel)] border p-3',
              st.id === 'email' ? 'border-dashed border-line bg-[var(--surface-muted)]' : 'border-line bg-card')}>
              <div className="mb-3">
                <div className="flex items-center gap-1.5 text-[length:var(--text-sm)] font-semibold text-ink">
                  {st.id === 'email' ? <Mail size={14} /> : <MessageCircle size={14} className="text-[#16a34a]" />} {st.title}
                </div>
                <div className="text-[length:var(--text-xs)] text-faint">{st.subtitle}</div>
              </div>
              <div className="grid gap-2">
                {st.msgs.map(m => {
                  const s = sum(statOf(m));
                  const readPct = s && s.entregados ? Math.round(s.leidos / s.entregados * 100) : null;
                  const muted = m.variant === 'fuera de la prueba' || (!m.templates.length);
                  return (
                    <button key={m.id} type="button" onClick={() => setOpen(m)}
                      className={cn('grid gap-1 rounded-[var(--radius-control)] border border-line bg-card p-2.5 text-left hover:border-[color:var(--foreground)]', muted && 'opacity-60')}>
                      <div className="flex items-start justify-between gap-2">
                        <span className="text-[length:var(--text-sm)] font-medium text-ink">{m.title}</span>
                        <ChevronRight size={14} className="mt-0.5 shrink-0 text-faint" />
                      </div>
                      <div className="flex flex-wrap items-center gap-1">
                        {m.variant && <Badge size="sm" tone={m.variant.startsWith('Epic') ? 'danger' : m.variant.startsWith('A/B') ? 'info' : 'neutral'}>{m.variant}</Badge>}
                        <span className="inline-flex items-center gap-1 text-[length:var(--text-xs)] text-soft"><Clock size={11} /> {m.when}</span>
                      </div>
                      {s && s.enviados > 0 && (
                        <div className="flex flex-wrap gap-x-2 text-[length:var(--text-xs)] tabular-nums text-faint">
                          <span><Send size={10} className="inline" /> {s.enviados}</span>
                          {readPct != null && <span><Eye size={10} className="inline" /> {readPct}% leído</span>}
                          {s.fallidos > 0 && <span className="text-[var(--danger)]"><AlertTriangle size={10} className="inline" /> {s.fallidos}</span>}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </section>
          </React.Fragment>
        ))}
      </div>
      <p className="mt-2 text-[length:var(--text-xs)] text-faint">Números: últimos 30 días · "% leído" sobre los entregados (Meta informa entrega/lectura desde el 18/10) · el costo usa las tarifas de Argentina por categoría.</p>

      <Dialog open={!!open} onOpenChange={v => { if (!v) setOpen(null); }}>
        <DialogContent title={open?.title || ''} description={open ? `${open.channel === 'email' ? 'Email' : 'WhatsApp'}${open.templates[0] && open.templates[0] !== '_texto' ? ' · ' + open.templates.join(' / ') : ''}` : ''}
          className="w-[min(620px,calc(100vw-32px))]">
          {open && (() => {
            const s = sum(statOf(open));
            const cat = cats.get(open.templates[0]);
            const body = open.templates.map(t => WA_TEMPLATE_BODIES[t]).find(Boolean);
            const usd = s ? costOf(open, s.enviados) : null;
            return (
              <div className="grid gap-4 text-[length:var(--text-sm)]">
                <div className="grid gap-1.5">
                  <div><b className="text-ink">Qué lo dispara:</b> <span className="text-soft">{open.trigger}</span></div>
                  <div><b className="text-ink">Cuándo sale:</b> <span className="text-soft">{open.when}</span></div>
                  {cat && <div><b className="text-ink">Categoría de Meta:</b> <span className="text-soft">{cat === 'MARKETING' ? 'Marketing (≈ USD 0,062 c/u)' : 'Utility (≈ USD 0,026 c/u)'}</span></div>}
                </div>
                {open.conditions?.length ? (
                  <div className="grid gap-1">
                    <b className="flex items-center gap-1.5 text-ink"><ShieldAlert size={14} /> Reglas</b>
                    <ul className="m-0 grid list-disc gap-0.5 pl-5 text-soft">{open.conditions.map((c, i) => <li key={i}>{c}</li>)}</ul>
                  </div>
                ) : null}
                {open.next?.length ? (
                  <div className="grid gap-1">
                    <b className="text-ink">Qué sigue</b>
                    <ul className="m-0 grid gap-0.5 pl-0 text-soft">{open.next.map((c, i) => <li key={i} className="flex gap-1.5"><ArrowRight size={13} className="mt-0.5 shrink-0" /> {c}</li>)}</ul>
                  </div>
                ) : null}
                {open.note && <p className="m-0 rounded-[var(--radius-control)] bg-[var(--surface-muted)] p-2.5 text-soft">{open.note}</p>}
                {body && (
                  <div className="grid gap-1">
                    <b className="text-ink">Texto</b>
                    <div className="whitespace-pre-wrap rounded-[var(--radius-control)] border border-line bg-[#e7fbe6] p-3 text-[length:var(--text-sm)] leading-relaxed text-[#111]">{body}</div>
                  </div>
                )}
                {s && s.enviados > 0 && (
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                    {[['Enviados', s.enviados], ['Entregados', s.entregados], ['Leídos', s.leidos], ['Fallidos', s.fallidos]].map(([l, v]) => (
                      <div key={l as string} className="rounded-[var(--radius-control)] border border-line p-2 text-center">
                        <div className="text-[length:var(--text-lg)] font-semibold tabular-nums text-ink">{Number(v).toLocaleString('es-AR')}</div>
                        <div className="text-[length:var(--text-xs)] text-faint">{l}</div>
                      </div>
                    ))}
                    {usd != null && <div className="col-span-2 text-[length:var(--text-xs)] text-faint sm:col-span-4">≈ USD {usd.toFixed(2)} en 30 días</div>}
                  </div>
                )}
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
